import { useCallback, useContext, useEffect, useState } from "react"
import { Button } from "@fc/devteam-components-library"
import { IJoinArgs } from "../../definitions"
import Avatar from "@components/profile/avatar"
import AgoraRTC from "agora-rtc-sdk-ng"
import { AgoraRTCError, AgoraRTCErrorCode, AgoraTrackType } from "../Agora/AgoraRtcCustomTypes"
import VideoPlayer from "../Videos/VideoPlayer"
import { CameraContext } from "../Contexts/cameraContext"
import Controls from "../Controls/controls"
import AgoraRegularManager from "../Agora/AgoraRegularManager"
import { getDefaultDevice } from "../Selector/utils"
import { getCameraConfig } from "../Utils/app"

interface ILobbyProps 
{
    fromTeleconsult?: boolean,
    onMediaEnabledChange?: (args: IJoinArgs) => void,
    canUseStream: (canUse: boolean) => void,
    join: (args: IJoinArgs) => void,
    showButton: boolean,
    username: string
}

interface ILobbyFlags 
{
    /**
     * This value will allow the component to know if the application 
     * has access to the media devices. If the value is `undefined`, it means that
     * the component doesn't know yet if the access has been granted
     */
    mediaEnabled: boolean | undefined,
    audioOnly: boolean,
    showVideo: boolean,
    showAudio: boolean
}

// TODO!: Change to 4 or 3 before deployment
AgoraRTC.setLogLevel(1)

const Lobby = (props: ILobbyProps) => {
    const { username } = props
    const { camera } = useContext(CameraContext)

    const [ manager, setManager ] = useState<AgoraRegularManager>()

    const [flags, setFlags] = useState<ILobbyFlags>({
        mediaEnabled: undefined,
        audioOnly: false,
        showVideo: false,
        showAudio: false
    })


    /**
     * Start webcam manager
     */
    useEffect(() => {
        if(!manager)
        {
            checkOnlyAudio().then(onlyAudio => {
                setFlags(current => current = {
                    ... current,
                    audioOnly: onlyAudio
                })
    
                const props = { uid: username }
                const mng = new AgoraRegularManager(props)
                mng.initialize({...getCameraConfig(), audioOnly: onlyAudio})
                .then(() => {
                    console.log("Client initialized")
                    setManager(mng)
                    setFlags(current => current = {
                        ... current,
                        mediaEnabled: true,
                        showVideo: !onlyAudio
                    })
                })
            })
        }

        return () => {
            if(!manager) return
            console.debug("Clean tracks", manager)
            // manager?.tracks?.forEach(track => AgoraManager.destroyTrack(track))
            manager.destroy()
        }
    }, [manager, manager?.tracks])

    useEffect(() => {
        if(!manager?.tracks) return;
        if(!camera) return;

        manager.tracks[AgoraTrackType.VIDEO]?.setDevice(camera.deviceId);
    }, [camera, manager?.tracks])

    useEffect(() => {
        // Dismiss on first load
        if(flags.mediaEnabled !== undefined)
            props.canUseStream(flags.mediaEnabled)
    }, [flags.mediaEnabled])

    useEffect(() => {
        console.debug("Manager tracks", manager)
        if(manager?.tracks && manager.tracks.length > 0)
        {
            // TODO: Remove this any. This validation will be valid since it was validated in condition before this line
            setFlags(current => current = {
                ... current,
                mediaEnabled: true,
                showVideo: (manager as any).tracks[AgoraTrackType.AUDIO]?.enabled || false,
                showAudio: (manager as any).tracks[AgoraTrackType.VIDEO]?.enabled || false
            })
        }
        else
        {
            // Dismiss on first load
            if(flags.mediaEnabled === undefined) return;
            setFlags(current => current = {
                ... current,
                mediaEnabled: false,
                showVideo: false,
                showAudio: false
            })
        }
    }, [manager?.tracks])

    useEffect(() => {
        if(!flags.mediaEnabled) return
        if(props.onMediaEnabledChange)
        {
            props.onMediaEnabledChange({
                video: flags.showVideo,
                audio: flags.showAudio,
                audioOnly: flags.audioOnly
            })
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [flags.mediaEnabled, flags.showVideo, flags.showAudio, flags.audioOnly])

    const handleError = (err: AgoraRTCError) => {
        if(!err?.code) return
        switch(err.code)
        {
            case AgoraRTCErrorCode.INVALID_OPERATION:
                console.warn("Invalid operation")

                break
            case AgoraRTCErrorCode.DEVICE_NOT_FOUND:
                console.error("Device not found", JSON.stringify(err))
                AgoraRTC.getCameras()
                    .then(cameras => console.log("Cameras available", cameras))
                    .catch(cameraError => console.error("Camera error", cameraError))
                break
            case AgoraRTCErrorCode.PERMISSION_DENIED:
                console.error("Could not get user media permission")
                setFlags(current => current = {
                    ... current,
                    mediaEnabled: false
                })
                break
            default:
                console.error("Unhandled Agora error", err)
                break
        }
    }
    
    /**
     * Check if the device only supports audio but not video
     */
    const checkOnlyAudio = useCallback(async() => {
        try
        {
            const defaultCamera = await getDefaultDevice("videoinput")
            return defaultCamera ? false : true
        }
        catch(error)
        {
            console.warn("Audio only")
            return true
        }
    }, [])

    const onMute = async(type: "audio" | "video", enable: boolean) => {
        if(!manager?.tracks) return
        if (type === "audio")
        {
            if(!flags.audioOnly)
                await manager.tracks[AgoraTrackType.AUDIO].setEnabled(enable)
            else
            {
                console.warn("onMute - audioOnly - This is not implemented yet")
                // micTracks.track?.setEnabled(enable)
            }
            setFlags(current => current = {
                ... current,
                showAudio: enable
            })
        }
        else if (type === "video")
        {
            if(!flags.audioOnly)
            {
                await manager.tracks[AgoraTrackType.VIDEO]?.setEnabled(enable)
                setFlags(current => current = {
                    ... current,
                    showVideo: enable
                })
            }
        }
    }

    const onJoin = () => {
        if(!flags.audioOnly)
        {
            if(!manager?.tracks) return
            props.join({
                audio: manager.tracks[AgoraTrackType.AUDIO].enabled,
                video: manager.tracks[AgoraTrackType.VIDEO]?.enabled || false
            })
        }
        else
        {
            console.warn("Audio only is not working now")

            if(!manager?.tracks) return
            props.join({
                audio: manager.tracks[AgoraTrackType.AUDIO].enabled,
                video: false,
                audioOnly: true
            })
        }
    }

    return (
        <div className="VideoCall items__center justify-center h-100">
            {/* The className="VideoCall" is for presenting a preview when doing a call */}
            <div className="VideoCall__Preview">
                <div className="VideoCall__Info">
                    {
                        !flags.mediaEnabled ?
                            <p className="VideoCall__Text">La cámara y el micrófono están desactivados</p>
                        :
                        manager?.tracks && manager.tracks.length > 0 && flags.showVideo ?
                            <VideoPlayer
                                className="vid"
                                videoTrack={manager.tracks[AgoraTrackType.VIDEO]}
                                style={{ height: "100%", width: "100%" }}
                            />
                            :
                            <div className="VideoCall__Info">
                                <div className="VideoCall__Avatar">
                                    <Avatar  
                                        username={props.username}
                                        roleCode="PR"
                                        size={30}
                                        editable={false}
                                    />
                                </div>
                            </div>
                    }
                </div>
                {
                    <Controls
                        onMute={onMute}
                        audioOnly={flags.audioOnly}
                        canShare={false}
                    />
                }
            </div>
            {
                flags.mediaEnabled && props.showButton &&
                <Button
                    buttontype="button"
                    buttontext={ props.fromTeleconsult ? "Iniciar Teleconsulta" : "Iniciar Videollamada"}
                    action={onJoin}
                />
            }
        </div>
    )
}

export default Lobby
